import { createContext, useContext } from 'react';
import { ChatConversation } from '../features/chat/hooks/types';
import { WhatsappLiveStatus } from '../features/chat/hooks/useChatConnection';
import type {
    ContactCardInput,
    LocationMessageInput,
    PixMessageInput,
} from '../features/chat/hooks/useChatSpecialActions';
import { WhatsappConnection } from '../types';

export interface ChatContextType {
    conversations: ChatConversation[];
    activeConversation: ChatConversation | undefined;
    activeConversationId: string | null;
    activeConnectionId?: string | null;
    setActiveConversationId: (id: string | null, connectionId?: string | null) => void;
    connections: WhatsappConnection[];
    selectedConnection: WhatsappConnection | undefined;
    selectedConnectionId: string | null;
    setSelectedConnection: (id: string) => void;
    setSelectedConnectionId: (id: string) => void;
    refreshConnections: () => Promise<void>;
    startNewConversation: (number: string, name: string) => void;
    sendMessage: (text: string, quotedMessage?: { id: string; sender: string; text?: string }) => void;
    sendMedia: (file: File) => void;
    sendContactCard: (input: ContactCardInput) => Promise<void>;
    sendLocationMessage: (input: LocationMessageInput) => Promise<void>;
    sendPixMessage: (input: PixMessageInput) => Promise<void>;
    deleteMessage: (messageId: string) => void;
    editMessage: (messageId: string, newText: string, originalText?: string) => void | Promise<void>;
    assignConversation: (conversationId: string) => Promise<boolean>;
    transferConversation: (conversationId: string, targetUserId: string) => Promise<boolean>;
    removeAssignment: (conversationId: string) => Promise<boolean>;
    closeConversation: (conversationId: string) => Promise<boolean>;
    reopenConversation: (conversationId: string) => Promise<boolean>;
    updateConversation: (
        conversationId: string,
        updates: Partial<ChatConversation>,
        connectionId?: string | null,
    ) => void;
    currentUserId: string | null;
    isLoading: boolean;
    isLoadingConnections?: boolean;
    isLoadingMessages?: boolean;
    error: string | null;
    clearError: () => void;
    retryLastMessage: () => Promise<void>;
    isRetrying: boolean;
    loadOlderMessages?: () => Promise<boolean>;
    isLoadingOlder?: boolean;
    hasMoreMessages?: boolean;
    isLoadingMoreConversations?: boolean;
    hasMoreConversations?: boolean;
    loadMoreConversations?: () => void;
    backendCounters?: { unassigned: number; mine: number; all: number };
    whatsappLive: WhatsappLiveStatus;
    isPeerTyping?: boolean;
    sendTyping?: () => void;
    /** Filtro de canal (WA/IG) — escopo server-side da lista. */
    inboxChannelConnectionId?: string | null;
    setInboxChannelConnectionId?: (id: string | null) => void;
}

export const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function useChatContext(): ChatContextType {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChatContext must be used within a ChatProvider');
    }
    return context;
}
